import { normalizeTimestamp } from './normalization.js';

function clean(value) {
  if (value === undefined || value === null) return '';
  return String(value).trim();
}

function toBool(value) {
  const v = clean(value).toLowerCase();
  if (['true', 'yes', '1', 'y'].includes(v)) return true;
  if (['false', 'no', '0', 'n'].includes(v)) return false;
  return null;
}

function toNumber(value) {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function indexSideRowsByTask(rows = []) {
  const map = new Map();
  for (const row of rows || []) {
    const key = clean(row?.task || row?.caseKey || row?.case_key || row?.issueKey || row?.number);
    if (!key) continue;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  }
  return map;
}

export function normalizeComments(rows = []) {
  return (rows || []).map((row, index) => ({
    id: clean(row?.id) || `comment-${index + 1}`,
    timestamp: normalizeTimestamp(row?.timestamp || row?.created || row?.sys_created_on),
    author: clean(row?.author || row?.user || 'Unknown') || 'Unknown',
    visibility: toBool(row?.jsdPublic) === true ? 'public' : toBool(row?.jsdPublic) === false ? 'internal' : 'unknown',
    accountType: clean(row?.accountType || row?.account_type),
    source: {
      nativeId: clean(row?.sys_id || row?.commentId || row?.id),
    },
  }));
}

export function normalizeWorklogs(rows = []) {
  return (rows || []).map((row, index) => ({
    id: clean(row?.id) || `worklog-${index + 1}`,
    timestamp: normalizeTimestamp(row?.timestamp || row?.created || row?.sys_created_on),
    author: clean(row?.author || row?.user || 'Unknown') || 'Unknown',
    timeSpentSeconds: toNumber(row?.timeSpentSeconds ?? row?.time_spent_seconds ?? row?.seconds),
    source: {
      nativeId: clean(row?.sys_id || row?.worklogId || row?.id),
    },
  }));
}

export function buildSlaExtension(caseAttributes = {}, caseAttributeDefinitions = []) {
  const byId = new Map((caseAttributeDefinitions || []).map(def => [String(def?.id || ''), def]));
  const fields = {};

  for (const [attrId, value] of Object.entries(caseAttributes || {})) {
    const def = byId.get(String(attrId)) || {};
    const field = clean(def.sourceField || def.name || def.attributeID || def.field || def.label || attrId);
    if (!field || !field.toLowerCase().startsWith('sla_')) continue;
    fields[field] = value;
  }

  if (!Object.keys(fields).length) return null;

  const names = clean(fields.sla_names).split('|').map(v => v.trim()).filter(Boolean);
  const stages = clean(fields.sla_stages).split('|').map(v => v.trim()).filter(Boolean);

  return {
    available: toNumber(fields.sla_total_count) !== null || Object.keys(fields).length > 0,
    totalCount: toNumber(fields.sla_total_count),
    breachedCount: toNumber(fields.sla_breached_count),
    hasBreach: toBool(fields.sla_has_breach),
    activeCount: toNumber(fields.sla_active_count),
    pausedCount: toNumber(fields.sla_paused_count),
    completedCount: toNumber(fields.sla_completed_count),
    cancelledCount: toNumber(fields.sla_cancelled_count),
    businessElapsedHours: toNumber(fields.sla_business_elapsed_hours),
    businessTimeLeftHours: toNumber(fields.sla_business_time_left_hours),
    pauseHours: toNumber(fields.sla_pause_hours),
    maxBusinessPercentage: toNumber(fields.sla_max_business_percentage),
    names,
    stages,
    raw: fields,
  };
}

export function buildCaseExtensions({
  caseKey,
  caseAttributes = {},
  caseAttributeDefinitions = [],
  commentsByTask,
  worklogsByTask,
} = {}) {
  const comments = normalizeComments(commentsByTask?.get(String(caseKey)) || []);
  const worklogs = normalizeWorklogs(worklogsByTask?.get(String(caseKey)) || []);
  const sla = buildSlaExtension(caseAttributes, caseAttributeDefinitions);

  const extensions = {};
  if (comments.length || worklogs.length) {
    extensions.engagement = {
      comments,
      worklogs,
      commentCount: comments.length,
      publicCommentCount: comments.filter(c => c.visibility === 'public').length,
      internalCommentCount: comments.filter(c => c.visibility === 'internal').length,
      worklogCount: worklogs.length,
      totalLoggedSeconds: worklogs.reduce((sum, item) => sum + (Number(item.timeSpentSeconds) || 0), 0),
    };
  }
  if (sla) extensions.sla = sla;
  return extensions;
}
